import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import FeatureUtils from '@appblocks/codegeneration/FeatureUtils';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
    SettingOption,
    AppBlockPort,
    AppBlockLinkParameter,
} from '@appblocks/codegeneration/types';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import NodeArgument from '@appblocks/codegeneration/Nodes/NodeArgument';
import * as tios from './tios';
import * as simulator from './simulator';

export default class TableInsertNode extends BaseNode {
    constructor() {
        super();
        this.name = 'Table Insert Row';
        this.id = 'node_table_insert';
        this.icon = 'HiOutlineDatabase';
        this.requirements = ['tios'];
        this.linkedFeature = 'general';
        this.inputs = [
            {
                name: 'Add Row',
                portId: 'tbl_add',
                params: [],
            },
        ];

        const settings = [
            {
                id: 'TABLE',
                name: 'Table',
                settingType: SettingType.OPTION,
                refId: 'tbl_table',
                required: true,
                default: '',
                options: [] as any[],
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getInputs(): AppBlockPort[] {
        const project: ProjectConfiguration = this.project;
        const TABLE = project.getFeature(this.getSettingValue('TABLE'));
        if (TABLE === undefined) {
            this.inputs[0].params = [];
            return this.inputs;
        }
        const FIELDS = TABLE.getSettingValue('FIELDS');
        const fields = [];
        for (let i = 0; i < FIELDS.length; i += 1) {
            const field = FIELDS[i];
            fields.push({
                name: field.getSettingValue('NAME'),
                dataType: TibboDataType.STRING,
                default: field.getSettingValue('DEFAULT'),
            });
        }
        this.inputs[0].params = fields;
        return this.inputs;
    }

    validate(): ValidationMessage[] {
        const errors: ValidationMessage[] = [];
        const project: ProjectConfiguration = this.project;
        const TABLE = project.getFeature(this.getSettingValue('TABLE'));
        if (TABLE === undefined) {
            const ARGUMENTS: NodeArgument[] = this.getSettingValue('ARGUMENTS');
            for (let i = 0; i < ARGUMENTS.length; i++) {
                const arg: NodeArgument = ARGUMENTS[i];
                errors.push({
                    messageType: ValidationMessageType.REMOVE,
                    message: '',
                    featureId: arg.getID(),
                    settingId: undefined,
                });
            }
            return errors;
        }
        const FIELDS = TABLE.getSettingValue('FIELDS');
        const fields = [];
        for (let i = 0; i < FIELDS.length; i += 1) {
            const field = FIELDS[i];
            fields.push({
                name: field.getSettingValue('NAME'),
                dataType: TibboDataType.STRING,
            });
        }
        const ARGUMENTS: NodeArgument[] = this.getSettingValue('ARGUMENTS');
        const inputs = this.getInputs();
        const params = inputs[0].params;

        for (let i = 0; i < params.length; i++) {
            const param = params[i];
            const arr = ARGUMENTS.filter((arg: BaseFeature) => arg.getSettingValue('TARGETPARAM') === param.name);
            if (arr.length === 0) {
                const arg = new NodeArgument();
                arg.initSettings(project);
                arg.setSettingValue('TARGETPARAM', param.name);
                arg.setSettingValue('ARGVALUE', '');
                errors.push({
                    messageType: ValidationMessageType.ADD,
                    message: '',
                    featureId: this.getID(),
                    settingId: 'ARGUMENTS',
                    data: FeatureUtils.toJson(arg),
                });
            }
        }
        for (let i = 0; i < ARGUMENTS.length; i++) {
            const arg: NodeArgument = ARGUMENTS[i];
            const arr = params.filter((param: AppBlockLinkParameter) => param.name === arg.getSettingValue('TARGETPARAM'));
            if (arr.length === 0) {
                errors.push({
                    messageType: ValidationMessageType.REMOVE,
                    message: '',
                    featureId: arg.getID(),
                    settingId: undefined,
                });
            }
        }
        return errors;
    }

    getSettingValue(settingId: string) {
        if (settingId === 'DISPLAY_NAME') {
            const project: ProjectConfiguration = this.project;
            const TABLE = project.getFeature(this.getSettingValue('TABLE'));
            if (TABLE) {
                return `Insert row into {{$${TABLE.getSettingValue('NAME')}}}`;
            }
        }
        return super.getSettingValue(settingId);
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'simulator':
                return simulator.getCodeBlocks(this);
            default:
                return tios.getCodeBlocks(this);
        }
    }
}
